import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import { useForm } from 'hooks';
import {
  setDiscountCode,
  setPayablePrice,
  setUseCredit,
  validateDiscountCodeStart,
} from 'redux/payments/payments.actions';
import { splitPriceByThree } from 'utils';
import { validateLeftSideBox } from './validateLeftSideBox';
import styles from './LeftSideBox.module.scss';
import { useEffect } from 'react';
import useTranslation from 'hooks/useTranslation';
import { replaceKeyWithValue } from 'utils/text';
import { calculatePrices } from 'utils/packages';

const mapState = (state) => ({
  packages: state.paymentsData.packages,
  selectedPackage: state.paymentsData.selectedPackage,
  selectedPackageType: state.paymentsData.selectedPackageType,
  selectedDuration: state.paymentsData.selectedDuration,
  discountInfo: state.paymentsData.discountInfo,
  discountCode: state.paymentsData.discountCode,
  useCredit: state.paymentsData.useCredit,
  userCredit: state.userData.profile.userCredit,
});

const LeftSideBox = ({ isOfficialBillNeeded, price }) => {
  const dispatch = useDispatch();
  const {
    packages,
    selectedPackage,
    selectedPackageType,
    selectedDuration,
    discountInfo,
    discountCode,
    useCredit,
    userCredit,
  } = useSelector(mapState);
  const selectedPack = packages.data[selectedPackage];
  const { getTranslation, getAppLocale } = useTranslation('PACKAGE_UPGRADING');
  const { t } = getTranslation();

  const { totalPrice, postPrice, tax, discountPercent, originaTotalPrice } =
    calculatePrices({
      price: price,
      isOfficialBillNeeded,
      useCredit,
      userCredit,
      discountInfo,
      selectedPackageType,
      selectedDuration,
      locale: getAppLocale(),
    });

  const locale = getAppLocale();

  const handleValidateDiscountCode = (values) => {
    dispatch(
      validateDiscountCodeStart({
        code: values.discountCode,
        selectedPackageType,
      })
    );
  };

  const { values, errors, handleChange, handleSubmit } = useForm(
    validateLeftSideBox,
    handleValidateDiscountCode,
    { discountCode: '' }
  );

  const handleCancleDiscountCode = () => {
    dispatch(setDiscountCode({ discountCode: '', discountInfo: null }));
  };

  const handleSetUseCredit = (event) => {
    dispatch(setUseCredit(event.target.checked));
  };

  useEffect(() => {
    handleCancleDiscountCode();
    handleSetUseCredit({ target: { checked: false } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(setPayablePrice(totalPrice < 0 ? 0 : totalPrice));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedPackage,
    selectedDuration,
    useCredit,
    isOfficialBillNeeded,
    discountInfo,
  ]);

  return (
    <>
      <ul className={styles.leftSideboxWrapper}>
        <li>
          <span className={styles.name}>{t('PACKAGE')}</span>
          <span className={styles.value}>
            {selectedPack.name[getAppLocale()]}
          </span>
        </li>
        <li>
          <span className={styles.name}> {t('DURATION')} </span>
          <span className={styles.value}>
            {selectedDuration === 'yearly'
              ? t('YEARLY')
              : selectedDuration === 'triennial'
              ? t('TRIENNIAL')
              : selectedDuration === 'quarterly'
              ? t('QUARTERLY')
              : t('MONTHLY')}
          </span>
        </li>
        {locale === 'fa' && (
          <li>
            <span className={styles.name}> {t('POSTING')} </span>
            <span className={styles.value}>{splitPriceByThree(postPrice)}</span>
          </li>
        )}
        <li>
          <span className={styles.name}>{t('TAX')}</span>
          <span className={styles.value}>
            {splitPriceByThree(tax < 0 ? 0 : Math.round(tax))}
          </span>
        </li>
      </ul>
      <div className={styles.actionWrapper}>
        <div className={styles.useCreditWrapper}>
          <div className={styles.formControl}>
            <input
              type='checkbox'
              id='useCredit'
              onChange={handleSetUseCredit}
              disabled={userCredit === 0 ? true : !discountCode ? false : true}
              checked={useCredit}
            />
            <label htmlFor='useCredit' aria-describedby='label'>
              {t('USE_CREDIT')}
            </label>
          </div>
          <span>
            ( {splitPriceByThree(userCredit)}{' '}
            {getAppLocale() === 'fa' ? t('TOOMAN') : t('UNIT')} )
          </span>
        </div>
        <div className={styles.inputWrapper}>
          {(!discountInfo || (discountInfo && !discountInfo.offPrice)) && (
            <>
              <input
                name='discountCode'
                value={values.discountCode}
                onChange={handleChange}
                className={clsx(errors.discountCode && 'has-error')}
                disabled={useCredit ? true : false}
                type='text'
                placeholder={t('ENTER_DISCOUNT_CODE')}
              />
              <button
                className={clsx(styles.btn, {
                  'btn-primary-disabled': useCredit,
                })}
                onClick={handleSubmit}
                disabled={useCredit ? true : false}
              >
                {t('CHECK')}
              </button>
            </>
          )}
          {errors.discountCode && (
            <div className={styles.error}>{t('ENTER_DISCOUNT_CODE')}</div>
          )}
          {discountInfo && discountInfo.offPrice && (
            <>
              <div className={styles.successfullDiscountCode}>
                {replaceKeyWithValue(
                  t('DISCOUNT_CODE_APPLIED'),
                  'code',
                  discountCode
                )}
                <button
                  onClick={handleCancleDiscountCode}
                  className={styles.btn}
                >
                  {t('CANCEL')}
                </button>
              </div>
            </>
          )}
        </div>
        <div className={styles.discountPriceBox}>
          {discountInfo && discountInfo.offPrice && (
            <>
              <span>
                % {Math.round(discountPercent < 0 ? 0 : discountPercent)}
              </span>
              <span>
                {splitPriceByThree(
                  originaTotalPrice < 0 ? 0 : originaTotalPrice
                )}{' '}
                {getAppLocale() === 'fa' ? t('TOOMAN') : t('UNIT')}
              </span>
            </>
          )}
        </div>

        <div className={styles.priceWrapper}>
          <span> {t('PAYABLE')} </span>
          <span>
            {splitPriceByThree(totalPrice < 0 ? 0 : Math.round(totalPrice))}{' '}
            {getAppLocale() === 'fa' ? t('TOOMAN') : t('UNIT')}
          </span>
        </div>
      </div>
    </>
  );
};

export default LeftSideBox;
